import React, { useState, useEffect, useRef } from "react";
import { Modal, Grid, Typography, Stack, Box } from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  NormalTextField,
  HeaderButton,
  DropdownTextField,
  DateTextField,
} from "../../../components";
import { MuiTelInput } from "mui-tel-input";
import dayjs from "dayjs";
import { api, urls } from "../../../services";
import { useToast, useAuth } from "../../../contexts";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    minWidth: 350,
    background: theme.palette.common.greyBg,
    boxShadow: 24,
    padding: theme.spacing(4),
    borderRadius: 6,
  },
  megazinePlanBox: {
    width: 300,
    border: "5px solid #ECECEC",
    padding: "20px",
    borderRadius: "4px",
  },
  discountBox: {
    background: "#EB363F12",
    color: "#EB363F",
    width: 170,
    fontSize: "12px",
    fontWeight: 500,
    padding: "10px",
    borderRadius: "4px",
  },
  titleText: {
    "&.MuiTypography-root": {
      fontSize: 20,
      fontWeight: 600,
    },
    display: "flex",
    justifyContent: "center",
    paddingBottom: theme.spacing(1),
  },
  subtitleText: {
    "&.MuiTypography-root": {
      fontSize: 12,
      color: "#757575",
      display: "flex",
      justifyContent: "center",
      paddingBottom: theme.spacing(3),
      textAlign: "center",
    },
  },
  statusLine: {
    "&.MuiTypography-root": {
      fontSize: 12,
      fontWeight: 500,
    },
  },
}));

export function AddUsers(props) {
  const classes = useStyles();
  const { open, onClose, onAddedUser, prefill } = props;
  const { showToast } = useToast();
  const { logout } = useAuth();
  const [formData, setFormData] = useState({
    name: null,
    email: null,
    phone_code: "+91",
    phone_number: null,
    plan: null,
    startDate: null,
    endDate: null,
    showError: true,
  });
  const [subscriptions, setSubscriptions] = useState([]);
  const [apiCalling, setApiCalling] = useState(false);
  const [userStatus, setUserStatus] = useState(null);
  const statusTimerRef = useRef(null);

  const handleCancelClick = () => {
    onAddedUser();
  };

  const handleSaveClick = () => {
    let params = {
      first_name: formData.name,
      email: formData.email ?? "",
      phone_code: formData.phone_code,
      phone_number: formData.phone_number,
      subscription_plan: formData.plan.id,
      plan_start_date: dayjs(formData.startDate).format("YYYY-MM-DD"),
      plan_end_date: dayjs(formData.endDate).format("YYYY-MM-DD"),
    };
    setApiCalling(true);
    api
      .post(urls.addUser, params)
      .then((res) => {
        setApiCalling(false);
        showToast(res.data.message, "success");
        onAddedUser();
      })
      .catch((err) => {
        setApiCalling(false);
        showToast(err.response?.data?.message, "error");
      });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (data) => {
    const { name, email, phone_code, phone_number, plan, startDate, endDate } =
      data;

    return (
      !name ||
      (email && !validateEmail(email)) ||
      !phone_code ||
      !phone_number ||
      !plan ||
      !startDate ||
      !endDate ||
      (startDate && endDate && new Date(startDate) > new Date(endDate))
    );
  };

  const handlePhoneInputChange = (value) => {
    let items = value.split(" ");
    let code = items[0];
    let phone = items.slice(1).join("");
    setFormData((prev) => {
      const updatedData = { ...prev, phone_code: code, phone_number: phone };
      return { ...updatedData, showError: validateForm(updatedData) };
    });
  };

  const handleNameInputChange = (e) => {
    setFormData((prev) => {
      const updatedData = { ...prev, name: e.target.value };
      return { ...updatedData, showError: validateForm(updatedData) };
    });
  };

  const handleEmailInputChange = (e) => {
    setFormData((prev) => {
      const updatedData = { ...prev, email: e.target.value };
      return { ...updatedData, showError: validateForm(updatedData) };
    });
  };

  const handleStartDateChange = (date) => {
    const startDate = new Date(date);

    // Calculate endDate by adding 365 days to startDate
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 365);

    setFormData((prev) => {
      const updatedData = { ...prev, startDate: startDate, endDate: endDate };
      return { ...updatedData, showError: validateForm(updatedData) };
    });
  };

  const handleEndDateChange = (date) => {
    setFormData((prev) => {
      const updatedData = { ...prev, endDate: date };
      return { ...updatedData, showError: validateForm(updatedData) };
    });
  };

  const handlePlanChange = (e, value) => {
    setFormData((prev) => {
      const updatedData = { ...prev, plan: value };
      return { ...updatedData, showError: validateForm(updatedData) };
    });
  };

  useEffect(() => {
    setApiCalling(true);
    api
      .get(`${urls.subscriptionList}?page=1&limit=50`)
      .then((res) => {
        const subs = res.data.data.subscription_plans;
        if (subs.length > 0) {
          setSubscriptions(subs);
          setFormData((prev) => {
            const updatedData = {
              ...prev,
              plan: subs[0],
            };
            return { ...updatedData, showError: validateForm(updatedData) };
          });
        }

        setApiCalling(false);
      })

      .catch((err) => {
        setApiCalling(false);
        if (err.response?.status === 401) {
          logout();
        } else {
          showToast(err.response?.data?.message, "error");
        }
      });
  }, []);

  // Prefill the phone when opened from a row's Renew button
  useEffect(() => {
    if (!prefill || !prefill.phone_code) return undefined;
    setFormData((prev) => {
      const updatedData = {
        ...prev,
        phone_code: prefill.phone_code,
        phone_number: prefill.phone_number,
      };
      return { ...updatedData, showError: validateForm(updatedData) };
    });
  }, [prefill]);

  // Live renewal status: look up the phone number as it is typed
  useEffect(() => {
    if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    setUserStatus(null);
    const digits = (formData.phone_number || "").replace(/\D/g, "");
    const code = (formData.phone_code || "").replace(/\D/g, "");
    if (!code || digits.length < 7) return undefined;
    statusTimerRef.current = setTimeout(() => {
      const params = new URLSearchParams({
        phone_code: code,
        phone_number: digits,
      });
      api
        .get(`${urls.userStatus}?${params.toString()}`)
        .then((res) => {
          setUserStatus(res.data?.data || null);
        })
        .catch(() => {});
    }, 600);
    return () => clearTimeout(statusTimerRef.current);
  }, [formData.phone_code, formData.phone_number]);

  const statusText = (() => {
    if (!userStatus) return null;
    if (!userStatus.exists) {
      return {
        color: "#2E7D32",
        text: "New user — will be added with the selected plan.",
      };
    }
    const name = userStatus.name ? `${userStatus.name} · ` : "";
    if (userStatus.window) {
      const w = userStatus.window;
      if (w.active) {
        return {
          color: "#ED6C02",
          text: `Existing user · ${name}already subscribed ${w.start} → ${w.end} (active). Save will be blocked.`,
        };
      }
      return {
        color: "#0288D1",
        text: `Existing user · ${name}subscription ended ${w.end}. Saving will RENEW it to the selected dates.`,
      };
    }
    return {
      color: "#0288D1",
      text: `Existing user · ${name}never subscribed. Saving will subscribe them.`,
    };
  })();

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={classes.root}>
        <Typography className={classes.titleText}>Add / Renew User</Typography>
        <Typography className={classes.subtitleText}>
          Adds a new user, or renews an existing one whose subscription has
          ended.
        </Typography>
        <Grid container spacing={4} direction="column">
          <Grid item xs>
            <Stack spacing={4}>
              <NormalTextField
                label="Name"
                value={formData.name}
                onChange={handleNameInputChange}
              />
              <NormalTextField
                label="Email (Optional)"
                value={formData.email}
                onChange={handleEmailInputChange}
              />

              <MuiTelInput
                label="Phone Number"
                sx={{ width: "100%" }}
                defaultCountry="IN"
                value={formData.phone_code + formData.phone_number}
                onChange={handlePhoneInputChange}
              />

              {statusText && (
                <Typography
                  className={classes.statusLine}
                  sx={{ color: statusText.color, mt: "-20px" }}
                >
                  {statusText.text}
                </Typography>
              )}

              <Box>
                <DropdownTextField
                  label={"Subscription Plan"}
                  options={subscriptions}
                  values={formData.plan}
                  noClearIcon={true}
                  optionKey={"name"}
                  onChange={handlePlanChange}
                />
              </Box>
              <Grid item xs>
                <Stack spacing={2} direction="row">
                  <DateTextField
                    label="Start Date"
                    onChange={handleStartDateChange}
                    value={
                      formData.startDate === null
                        ? null
                        : dayjs(formData.startDate)
                    }
                  />
                  <DateTextField
                    label="End Date"
                    onChange={handleEndDateChange}
                    value={
                      formData.endDate === null ? null : dayjs(formData.endDate)
                    }
                    minDate={formData.startDate}
                    disabled
                  />
                </Stack>
              </Grid>
              <Stack direction="row" spacing={2}>
                <HeaderButton type="secondary" onClick={handleCancelClick}>
                  Cancel
                </HeaderButton>
                <HeaderButton
                  type="primary"
                  onClick={handleSaveClick}
                  disabled={formData.showError || apiCalling}
                >
                  Save
                </HeaderButton>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}
